import styled, { css } from 'styled-components'
import React, { useEffect } from 'react'
import ReactDOM from 'react-dom'
import { Transition, TransitionStatus } from 'react-transition-group'
import getLayer from 'styles/layer'

interface TransitionProps {
  state: TransitionStatus
  position?: 'top' | 'center' | 'bottom'
}

const dialogWrapperAnimate = (props: TransitionProps) => {
  const { state } = props
  if (state === 'entered') {
    return css`
      opacity: 1;
      pointer-events: visible;
    `
  }
  return css`
    opacity: 0;
  `
}

const DialogWrapper = styled.div<TransitionProps>`
  position: fixed;
  left: 0;
  top: 0;
  right: 0;
  bottom: 0;
  height: 100vh;

  background-color: ${(p) => p.theme.DIALOG_OVERLAY};
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: ${getLayer('modal')};

  transition: all 0.3s ease-in-out;
  opacity: 0;
  pointer-events: none;
  ${dialogWrapperAnimate}
`

const dialogContentAnimate = (props: TransitionProps) => {
  const { state } = props
  if (state === 'entered') {
    return css`
      transform: translateY(0);
    `
  }
  return css`
    transform: translateY(-200px);
  `
}

export const DialogContent = styled.div<TransitionProps>`
  width: 100%;
  height: 100vh;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  ${(props) => props.position === 'center' && 'justify-content: center;'}
  ${(props) => props.position === 'top' && 'justify-content: flex-start;'}
  ${(props) => props.position === 'bottom' && 'justify-content: flex-end;'}
  align-items: center;
  transition: all 0.3s ease-in-out;
  transform: translateY(-200px);
  ${dialogContentAnimate}
`

const DialogHeader = styled.div<{ hasTitle?: boolean }>`
  display: flex;
  justify-content: ${(p) => (p.hasTitle ? 'space-between' : 'flex-end')};
  align-items: center;
  height: 72px;
  padding: 0 40px;
  border-bottom: ${(p) =>
    p.hasTitle ? `1px solid ${p.theme.BORDER_GRAY}` : 'none'};
  box-sizing: border-box;

  @media only screen and (max-width: ${(p) => p.theme.vp}px) {
    padding: 0 16px;
  }
`

const DialogTitle = styled.h2`
  font-style: normal;
  font-weight: 700;
  font-size: ${(p) => p.theme.font.size.l};
  line-height: 24px;
  margin: 0;
  color: #463e28;

  @media only screen and (max-width: ${(p) => p.theme.vp}px) {
    font-size: ${(p) => p.theme.font.size.m};
    line-height: 20px;
  }
`

const CloseIconWrapper = styled.div`
  cursor: pointer;
  display: flex;
  justify-content: flex-end;

  @media only screen and (max-width: ${(p) => p.theme.vp}px) {
    img {
      width: 18px;
      height: 18px;
    }
  }
`

const DialogBodyContent = styled.div<{ hasTitle?: boolean }>`
  padding: ${(p) => (p.hasTitle ? '40px' : '0 40px 40px 40px')};

  @media only screen and (max-width: ${(p) => p.theme.vp}px) {
    padding: ${(p) => (p.hasTitle ? '16px' : '0 16px 16px 16px')};
  }
`

const DialogFooter = styled.div`
  height: 80px;
  padding: 0 40px;
  border-top: 1px solid ${(p) => p.theme.BORDER_GRAY};
  box-sizing: border-box;
  display: flex;
  align-items: center;

  @media only screen and (max-width: ${(p) => p.theme.vp}px) {
    padding: 0 16px;
  }
`

interface DialogBodyProps {
  height?: number
}

const DialogBody = styled.div<DialogBodyProps>`
  width: 640px;
  margin: 100px 25px 100px;
  background: #ffffff;
  border-radius: 10px;
  @media only screen and (max-width: ${(p) => p.theme.vp}px) {
    width: 95vw;
    max-width: 640px;
  }
`

export interface ModalProps {
  show?: boolean
  onClose: () => void
  children: React.ReactNode
  className?: string
  title?: string
  footer?: React.ReactNode
  disableClose?: boolean
  styleOptions?: {
    height?: number
    position?: 'top' | 'center' | 'bottom'
  }
}

/**
 * @deprecated Use DialogV2 instead of this component.
 */
const Dialog: React.FC<ModalProps> = ({
  show,
  onClose,
  children,
  className,
  title,
  footer,
  disableClose,
  styleOptions,
}) => {
  // モーダル表示時、一時的にbodyのスクロールを無効にする。
  useEffect(() => {
    if (show) {
      document.body.style.top = `-${window.scrollY}px`
      document.body.style.left = '0px'
      document.body.style.right = '0px'
      document.body.style.position = 'fixed'
    } else {
      const scrollY = document.body.style.top
      document.body.style.position = ''
      document.body.style.top = ''
      document.body.style.left = ''
      document.body.style.right = ''
      window.scrollTo(0, parseInt(scrollY || '0', 10) * -1)
    }
  }, [show])

  const nodeRef = React.useRef(null)
  const component = (
    <Transition
      in={show}
      nodeRef={nodeRef}
      unmountOnExit
      timeout={{ enter: 0, exit: 350 }}
    >
      {(state: TransitionStatus) => (
        <DialogWrapper
          onMouseDown={disableClose ? undefined : onClose}
          state={state}
          className={className}
          role="dialog"
          aria-label="backdrop"
        >
          <DialogContent
            onMouseDown={(e) => e.stopPropagation()}
            state={state}
            position={styleOptions?.position}
          >
            <DialogBody height={styleOptions?.height}>
              <DialogHeader hasTitle={!!title}>
                {title && <DialogTitle>{title}</DialogTitle>}
                <CloseIconWrapper
                  onClick={disableClose ? undefined : onClose}
                  style={{
                    opacity: disableClose ? 0.5 : 1,
                    cursor: disableClose ? 'not-allowed' : 'pointer',
                  }}
                >
                  <img src="/image/close.svg" alt="close" />
                </CloseIconWrapper>
              </DialogHeader>
              <DialogBodyContent hasTitle={!!title}>
                {children}
              </DialogBodyContent>
              {footer && <DialogFooter>{footer}</DialogFooter>}
            </DialogBody>
          </DialogContent>
        </DialogWrapper>
      )}
    </Transition>
  )

  return ReactDOM.createPortal(
    component,
    document.querySelector('#root') || document.body
  )
}

export default Dialog
